import { parseBoardStartDate } from "../timeDisplay";
import { underlyingEventKey } from "./underlyingEventIdentity";

export const getSelectionStartDate = (selection) => parseBoardStartDate(
  selection?.start_time_iso || selection?.start_time_obj || selection?.start_time || ""
);

export const isPregameSelectionStarted = (selection, now = new Date()) => {
  if (selection?.is_live) {
    return false;
  }

  const startDate = getSelectionStartDate(selection);
  return Boolean(startDate && startDate.getTime() <= now.getTime());
};

export const getStartedSelections = (selected, now = new Date()) => (
  (Array.isArray(selected) ? selected : []).filter(
    (selection) => isPregameSelectionStarted(selection, now)
  )
);

const getSelectionEventKey = (selection) => {
  const league = String(selection?.league || selection?.sport || "").trim().toLowerCase();
  const eventId = String(
    selection?.game_id ?? selection?.gameId ?? selection?.event_id ?? selection?.eventId ?? ""
  ).trim();

  return eventId ? underlyingEventKey(`${league}|game:${eventId}`) : "";
};

const selectionIsLiveOrStarted = (selection, now = new Date()) => {
  const liveValue = String(selection?.is_live ?? selection?.status ?? "").trim().toLowerCase();
  if (selection?.is_live === true || ["1", "true", "yes", "live", "in_progress"].includes(liveValue)) {
    return true;
  }

  const startDate = getSelectionStartDate(selection);
  return Boolean(startDate && startDate.getTime() <= now.getTime());
};

const getDistinctAthleteCount = (selected) => new Set(
  selected.map((selection) => String(
    selection?.athlete_id ?? selection?.athleteId ?? selection?.id ?? selection?.name ?? ""
  ).trim().toLowerCase()).filter(Boolean)
).size;

/**
 * Mirror checkout's live same-event concentration rule for immediate UX.
 * Checkout still verifies current provider event evidence authoritatively.
 */
export const getLiveSameEventLineupPolicy = (
  selected,
  userTier = null,
  wagerInput = 0,
  now = new Date(),
  options = {}
) => {
  const lineup = Array.isArray(selected) ? selected : [];
  if (lineup.length < 2) {
    return null;
  }

  const maxSameEventLegs = Math.min(2, Math.floor(lineup.length / 2));
  const eventGroups = new Map();

  lineup.forEach((selection) => {
    const eventKey = getSelectionEventKey(selection);
    if (!eventKey) {
      return;
    }

    const current = eventGroups.get(eventKey) || { selections: [], liveOrStarted: false };
    current.selections.push(selection);
    current.liveOrStarted = current.liveOrStarted || selectionIsLiveOrStarted(selection, now);
    eventGroups.set(eventKey, current);
  });

  const violation = [...eventGroups.entries()].find(([, group]) => (
    group.liveOrStarted && group.selections.length > maxSameEventLegs
  ));
  if (!violation) {
    return null;
  }

  const [eventKey, group] = violation;
  const parsedTier = Number.parseInt(userTier, 10);
  const lowStakesEligible =
    lineup.length === 2 &&
    group.selections.length === 2 &&
    getDistinctAthleteCount(lineup) === 2 &&
    (!Number.isFinite(parsedTier) || parsedTier <= 1);

  if (lowStakesEligible) {
    const maxWager = options?.verifiedExternalOddsCoverage ? 10 : 5;
    const wager = Number.parseFloat(wagerInput);
    const overLimit = Number.isFinite(wager) && wager > maxWager + 0.001;
    return {
      code: "live_same_event_low_stakes",
      eventKey,
      legCount: group.selections.length,
      maxSameEventLegs,
      maxWager,
      blocking: overLimit,
      title: "Live same-game entry",
      message: overLimit
        ? `Two-pick live same-game entries have a $${maxWager.toFixed(2)} maximum. The wager has been limited to $${maxWager}.`
        : `This two-pick live same-game entry is limited to $${maxWager.toFixed(2)}. Line changes are shown and the latest displayed line is used automatically.`,
    };
  }

  return {
    code: "live_same_event_limit",
    eventKey,
    legCount: group.selections.length,
    maxSameEventLegs,
    maxWager: null,
    blocking: true,
    title: "Live same-game limit",
    message: "Too many live selections are from the same game. Replace a pick with one from another game; no more than 2 picks may share a live game, and at least half the lineup must be from other games.",
  };
};

export const getStartedSelectionsRemovalNotice = (startedSelections) => {
  const athleteNames = [...new Set(
    (Array.isArray(startedSelections) ? startedSelections : [])
      .map((selection) => String(selection?.name || "").trim())
      .filter(Boolean)
  )];

  if (athleteNames.length === 1) {
    return `${athleteNames[0]}'s event has already started, so the selection was removed from your line-up.`;
  }

  if (athleteNames.length > 1) {
    const finalName = athleteNames[athleteNames.length - 1];
    const precedingNames = athleteNames.slice(0, -1).join(", ");
    return `The events for ${precedingNames} and ${finalName} have already started, so those selections were removed from your line-up.`;
  }

  return "One or more events have already started, so those selections were removed from your line-up.";
};

export const hasFullExactSgoQuoteCoverage = (quote, selectedCount) => {
  const count = Number(selectedCount);
  const pricing = quote?.sgo_pricing;
  const legs = Array.isArray(pricing?.legs) ? pricing.legs : [];
  if (count !== 2 || legs.length < 2) {
    return false;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const hasLiveLeg = legs.some((leg) => Boolean(leg?.is_live));
  const configuredLiveMaxAge = Number(pricing?.live_cache_max_age_seconds);
  const maxAgeSeconds = hasLiveLeg
    ? (Number.isFinite(configuredLiveMaxAge) && configuredLiveMaxAge > 0 ? configuredLiveMaxAge : 60)
    : 300;
  const coveredIndexes = new Set();
  legs.forEach((leg) => {
    const fetchedAt = Number(leg?.fetched_at);
    const status = String(leg?.status || "");
    const multiplier = Number(leg?.multiplier);
    if (
      Number.isFinite(fetchedAt) &&
      fetchedAt > 0 &&
      fetchedAt <= nowSeconds + 60 &&
      nowSeconds - fetchedAt <= maxAgeSeconds &&
      Number.isFinite(multiplier) &&
      multiplier > 0 &&
      ["fresh", "cached"].includes(status)
    ) {
      coveredIndexes.add(Number(leg?.index));
    }
  });

  return coveredIndexes.size >= 2;
};

export const getTwoLegStartTimeWagerCap = (selected, now = new Date(), options = {}) => {
  if (!Array.isArray(selected) || selected.length !== 2) {
    return null;
  }
  if (options?.verifiedExternalOddsCoverage) {
    return null;
  }

  const startTimes = selected
    .map(getSelectionStartDate)
    .filter(Boolean)
    .map((startDate) => startDate.getTime());
  if (startTimes.length === 0) {
    return null;
  }

  const millisecondsToStart = Math.min(...startTimes) - now.getTime();
  if (millisecondsToStart > 15 * 60 * 1000) {
    return null;
  }
  if (millisecondsToStart > 5 * 60 * 1000) {
    return 15;
  }
  if (millisecondsToStart >= 2 * 60 * 1000) {
    return 10;
  }

  return 5;
};

const SOCCER_LEAGUES = new Set([
  "soccer", "mls", "epl", "premier_league", "laliga", "seriea", "ligue1",
  "worldcup", "worldcup1h", "worldcup2h",
]);

const SOCCER_PROTECTED_PROP_FAMILY_STATS = new Set([
  "passes_attempted",
  "shots",
  "shots_on_goal",
  "clearances",
]);
const SOCCER_PROTECTED_PROP_FAMILY_LABEL = "Passes Attempted, Shots, Shots on Goal, and Clearances";

export const isSoccerProtectedPropFamilySelection = (selection) => {
  const league = String(selection?.league || selection?.sport || "").trim().toLowerCase();
  const statKey = String(selection?.stat_key || selection?.stat || "")
    .trim()
    .toLowerCase()
    .replace(/^(projected_|promo_|goblin_|demon_)/, "");

  return SOCCER_LEAGUES.has(league) && SOCCER_PROTECTED_PROP_FAMILY_STATS.has(statKey);
};

export const isSoccerPassesAttemptedSelection = isSoccerProtectedPropFamilySelection;

export const getSoccerPassesAttemptedLegViolation = (selected, userTier = null) => (
  Array.isArray(selected) &&
  selected.length < 3 &&
  (userTier === null || userTier === undefined || Number(userTier) !== 0) &&
  selected.some(isSoccerProtectedPropFamilySelection)
    ? `${SOCCER_PROTECTED_PROP_FAMILY_LABEL} selections are available only in lineups with 3 or more picks.`
    : null
);

export const getSoccerPassesAttemptedWagerCap = (selected, now = new Date()) => {
  if (!Array.isArray(selected) || selected.length < 2) {
    return null;
  }

  const startTimes = selected
    .filter((selection) => !selection?.is_live && isSoccerProtectedPropFamilySelection(selection))
    .map(getSelectionStartDate)
    .filter(Boolean)
    .map((startDate) => startDate.getTime());
  if (startTimes.length === 0) {
    return null;
  }

  const millisecondsToStart = Math.min(...startTimes) - now.getTime();
  if (millisecondsToStart > 30 * 60 * 1000) {
    return null;
  }

  return millisecondsToStart <= 15 * 60 * 1000 ? 5 : 10;
};
